import {
  pgTable,
  uuid,
  text,
  boolean,
  integer,
  jsonb,
  timestamp,
  index,
  uniqueIndex,
  check,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Ad Platform Types
// ---------------------------------------------------------------------------

export const AD_PLATFORMS = ["meta", "google", "tiktok"] as const;
export type AdPlatform = (typeof AD_PLATFORMS)[number];

export const AD_ACCOUNT_STATUSES = ["connected", "disconnected", "error", "rate_limited"] as const;
export type AdAccountStatus = (typeof AD_ACCOUNT_STATUSES)[number];

// ---------------------------------------------------------------------------
// Ad Accounts — one per platform per tenant
// ---------------------------------------------------------------------------
// OAuth tokens are encrypted at rest (DEK). NEVER log tokens.

export const adAccounts = pgTable("ad_accounts", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  platform: text("platform").notNull(),
  externalAccountId: text("external_account_id").notNull(),
  accountName: text("account_name").notNull(),
  currency: text("currency").notNull().default("USD"),
  timezone: text("timezone"),
  // OAuth tokens — encrypted at rest
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token"),
  tokenExpiresAt: timestamp("token_expires_at", tz),
  // Sync state
  status: text("status").notNull().default("connected"),
  lastSyncAt: timestamp("last_sync_at", tz),
  lastSyncError: text("last_sync_error"),
  syncRetryCount: integer("sync_retry_count").notNull().default(0),
  // Metadata
  scopes: text("scopes").array(),
  platformMetadata: jsonb("platform_metadata"),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  uniqueIndex("uq_ad_account_tenant_platform_ext").on(table.tenantId, table.platform, table.externalAccountId),
  index("idx_ad_accounts_tenant").on(table.tenantId),
  index("idx_ad_accounts_status").on(table.status),
  check("chk_ad_account_platform", sql`${table.platform} IN ('meta', 'google', 'tiktok')`),
  check("chk_ad_account_status", sql`${table.status} IN ('connected', 'disconnected', 'error', 'rate_limited')`),
]);

// ---------------------------------------------------------------------------
// Ad Campaigns — synced from platforms every 15 min
// ---------------------------------------------------------------------------

export const AD_CAMPAIGN_STATUSES = ["active", "paused", "archived", "deleted"] as const;
export type AdCampaignStatus = (typeof AD_CAMPAIGN_STATUSES)[number];

export const adCampaigns = pgTable("ad_campaigns", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  adAccountId: uuid("ad_account_id").notNull(),
  platform: text("platform").notNull(),
  externalCampaignId: text("external_campaign_id").notNull(),
  name: text("name").notNull(),
  status: text("status").notNull().default("active"),
  objective: text("objective"),
  // Budget
  dailyBudgetCents: integer("daily_budget_cents"),
  lifetimeBudgetCents: integer("lifetime_budget_cents"),
  // Aggregated metrics (from last sync)
  spendCents: integer("spend_cents").notNull().default(0),
  impressions: integer("impressions").notNull().default(0),
  clicks: integer("clicks").notNull().default(0),
  leads: integer("leads").notNull().default(0),
  // Platform-reported conversions (OVERCOUNTED — never use for TRUE ROAS)
  platformConversions: integer("platform_conversions").notNull().default(0),
  platformRevenueCents: integer("platform_revenue_cents").notNull().default(0),
  // TRUE metrics (from CRM attribution)
  trueConversions: integer("true_conversions").notNull().default(0),
  trueRevenueCents: integer("true_revenue_cents").notNull().default(0),
  // Dates
  startDate: timestamp("start_date", tz),
  endDate: timestamp("end_date", tz),
  lastSyncAt: timestamp("last_sync_at", tz),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  uniqueIndex("uq_ad_campaign_tenant_ext").on(table.tenantId, table.platform, table.externalCampaignId),
  index("idx_ad_campaigns_tenant").on(table.tenantId),
  index("idx_ad_campaigns_account").on(table.adAccountId),
  index("idx_ad_campaigns_platform").on(table.tenantId, table.platform),
  check("chk_ad_campaign_platform", sql`${table.platform} IN ('meta', 'google', 'tiktok')`),
  check("chk_ad_campaign_status", sql`${table.status} IN ('active', 'paused', 'archived', 'deleted')`),
]);

export const AD_KEYWORD_STATUSES = ["active", "paused", "archived", "deleted"] as const;
export type AdKeywordStatus = (typeof AD_KEYWORD_STATUSES)[number];

export const adKeywordMetrics = pgTable("ad_keyword_metrics", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  adAccountId: uuid("ad_account_id").notNull(),
  platform: text("platform").notNull(),
  externalCampaignId: text("external_campaign_id").notNull(),
  externalAdGroupId: text("external_ad_group_id").notNull(),
  externalKeywordId: text("external_keyword_id").notNull(),
  keywordText: text("keyword_text").notNull(),
  status: text("status").notNull().default("active"),
  matchType: text("match_type"),
  spendCents: integer("spend_cents").notNull().default(0),
  impressions: integer("impressions").notNull().default(0),
  clicks: integer("clicks").notNull().default(0),
  platformConversions: integer("platform_conversions").notNull().default(0),
  lastSyncAt: timestamp("last_sync_at", tz),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  uniqueIndex("uq_ad_keyword_metrics_tenant_account_ad_group_keyword").on(
    table.tenantId,
    table.platform,
    table.adAccountId,
    table.externalAdGroupId,
    table.externalKeywordId,
  ),
  index("idx_ad_keyword_metrics_tenant").on(table.tenantId),
  index("idx_ad_keyword_metrics_account").on(table.adAccountId),
  index("idx_ad_keyword_metrics_campaign").on(table.tenantId, table.platform, table.externalCampaignId),
  check("chk_ad_keyword_metrics_platform", sql`${table.platform} IN ('meta', 'google', 'tiktok')`),
  check("chk_ad_keyword_metrics_status", sql`${table.status} IN ('active', 'paused', 'archived', 'deleted')`),
]);
